/**
 * Every field one MF record carries.
 *
 * The tables show a curated handful of columns; this shows the rest, exactly
 * as the API returned them, so nothing is out of reach while the record shapes
 * are still being confirmed. "Copy JSON" hands the raw record to whoever needs
 * it — support, or the backend.
 */

import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { MFRecord } from "../../../Api/mfAdminInvestments";
import { humanizeKey } from "./mfOverviewFormat";
import styles from "./mf-overview.module.css";

/** Credentials never belong on screen, whatever the payload carries. */
const HIDDEN_KEY = /password/i;

const FieldValue = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === "") {
    return <span className={styles.detailEmpty}>—</span>;
  }
  if (typeof value === "boolean") return <>{value ? "Yes" : "No"}</>;
  if (typeof value === "object") {
    return <pre className={styles.detailJson}>{JSON.stringify(value, null, 2)}</pre>;
  }
  return <>{String(value)}</>;
};

const RecordDetailsDialog = ({
  record,
  title,
  subtitle,
  onClose,
}: {
  record: MFRecord | null;
  title: string;
  subtitle: string;
  onClose: () => void;
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [record]);

  const keys = record ? Object.keys(record).filter((key) => !HIDDEN_KEY.test(key)) : [];

  const copy = async () => {
    if (!record) return;
    const visible: MFRecord = {};
    keys.forEach((key) => {
      visible[key] = record[key];
    });
    try {
      await navigator.clipboard.writeText(JSON.stringify(visible, null, 2));
      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Dialog open={Boolean(record)} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent className={styles.detailDialog}>
        <div className={styles.detailHead}>
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <div className={styles.detailHeadActions}>
            <button type="button" className={styles.ghostButton} onClick={copy}>
              <ContentCopyIcon style={{ fontSize: 15 }} aria-hidden="true" />
              {copied ? "Copied" : "Copy JSON"}
            </button>
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
        <dl className={styles.detailList}>
          {keys.map((key) => (
            <div key={key} className={styles.detailRow}>
              <dt>
                {humanizeKey(key)}
                <small>{key}</small>
              </dt>
              <dd>
                <FieldValue value={record ? record[key] : undefined} />
              </dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
};

export default RecordDetailsDialog;
